import moment from "moment-timezone";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    GuxButton,
    GuxButtonMulti,
    GuxIcon,
    GuxListItem,
    GuxTooltip,
} from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";
import "./SubmitActionOperations.less";
import strings from "./footer.strings";
import { useStrings } from "features/i18n/useStrings";
import { useContext } from "react";
import { HasEditScoreOrEditPermissionsContext } from "features/qualityForm/common/Contexts";
import {
    cancelEvaluationRescoring,
    createCalibration,
    deleteScorable,
    reassignScorable,
    saveEvaluationReleaseDate,
    scorableSliceActions,
    selectHasAssignPermission,
    selectHaveDisputePermission,
    selectHaveDisputeDeclinePermission,
    selectIsCalibration,
    selectIsConversationAgeLimitReached,
    selectIsDisputeLimitReached,
    selectIsScorableFinished,
    selectReleaseDate,
    UNASSIGNED,
    selectIsOperationInProgress,
} from "features/scorable/scorableSlice";
import { DefaultLimits } from "constants/DefaultLimits";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import embeddingService from "services/embeddingService";
import toastService from "services/toastService";
import FormType from "enums/FormType";
import { ReleaseDateOptions } from "enums/ReleaseDateOptions";
import { SideDrawerMode } from "enums/SideDrawerMode";
import { selectFormName } from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { selectCalibrationEvaluators } from "features/scorable/ScorableFormValidations";
import { usersSliceActions } from "features/users/usersSlice";
import { User } from "@genesys/cloud-frontend/dist/mjs/models";
import { SearchResult } from "features/qualityForm/formEditor/sidePanel/SearchDropdown";
import {
    getCurrentReleaseDate,
    ReleaseDateSelectedType,
} from "features/qualityForm/formViewer/footer/releaseEvaluationTime/ReleaseEvaluationSelector";
import { ErrorCodes } from "enums/ErrorCodes";
import React from "react";
import { useTimeZone } from "features/timezone/useTimeZone";

export function ApplyButton({
    currentAssignee,
    setSideDrawerMode,
}: {
    currentAssignee: User;
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const evaluationTitle = useAppSelector(selectFormName);
    const dropdownHasNoAssignee = currentAssignee.id === "";
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return (
        <GuxButton
            accent="primary"
            aria-disabled={dropdownHasNoAssignee || isOperationInProgress}
            disabled={dropdownHasNoAssignee || isOperationInProgress}
            onClick={() => {
                dispatch(reassignScorable(currentAssignee)).then((response) => {
                    if ("error" in response) {
                        const payload: any = response.payload ?? {};
                        if (
                            payload.message ===
                            ErrorCodes.USER_CANNOT_UPDATE_PRIVATE_COMMENTS_ERROR
                        ) {
                            toastService.notifyMajorAlert(
                                t.errors.privateCommentsUpdateFail
                            );
                        } else {
                            toastService.notifyMajorAlert(
                                t.errors.evaluationUpdateFail
                            );
                        }
                    } else {
                        toastService.notifyMinorAlert(
                            currentAssignee.id === UNASSIGNED
                                ? t.assignEvaluation.unassignSuccess({
                                      evaluationTitle: evaluationTitle ?? "",
                                  })
                                : t.assignEvaluation.success.message({
                                      evaluationTitle: evaluationTitle ?? "",
                                      assigneeName: currentAssignee.name ?? "",
                                  })
                        );
                        // update the assignee on the eval
                        dispatch(
                            scorableSliceActions.setAssignee(currentAssignee)
                        );
                        // reset the dropdown users
                        dispatch(usersSliceActions.clearUsers());
                        // close the side drawer
                        setSideDrawerMode(SideDrawerMode.OFF);
                    }
                });
            }}
        >
            {t.labels.apply}
        </GuxButton>
    );
}

export function OpenAssignViewButton({
    setSideDrawerMode,
}: {
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
}) {
    const t = useStrings(strings);
    const hasQualityEvaluationAssignPermissions = useAppSelector(
        selectHasAssignPermission
    );
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });

    const now = moment();
    const isScorableFinished = useAppSelector(selectIsScorableFinished);
    const releaseDate = useAppSelector(selectReleaseDate);
    const showAssignButton =
        !isScorableFinished ||
        releaseDate === undefined ||
        moment(releaseDate).isAfter(now);

    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return hasQualityEvaluationAssignPermissions &&
        isNotCalibration &&
        showAssignButton ? (
        <div>
            <GuxButton
                accent="tertiary"
                id="assign-button"
                aria-disabled={hasScoringExpired || isOperationInProgress}
                disabled={hasScoringExpired || isOperationInProgress}
                onClick={() => setSideDrawerMode(SideDrawerMode.ASSIGN)}
            >
                <GuxIcon iconName="message-forward" decorative={true}></GuxIcon>
                {t.labels.assign}
            </GuxButton>
            {hasScoringExpired ? (
                <GuxTooltip>{t.labels.tooltips.assigneeExpired}</GuxTooltip>
            ) : (
                <></>
            )}
        </div>
    ) : (
        <></>
    );
}

export function DisputeButton({
    attemptDispute,
}: {
    attemptDispute?: () => void;
}) {
    const t = useStrings(strings);

    const hasDisputingExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.disputeExpiry
        );
    });

    const hasDisputeLimitReached = useAppSelector(selectIsDisputeLimitReached);

    const hasDisputePermission = useAppSelector(selectHaveDisputePermission);
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const showDisputeButton = hasDisputePermission && isNotCalibration;
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return showDisputeButton ? (
        <GuxButton
            accent="tertiary"
            id="dispute-button"
            onClick={() => {
                attemptDispute?.();
            }}
            disabled={
                attemptDispute === undefined ||
                hasDisputingExpired ||
                hasDisputeLimitReached ||
                isOperationInProgress
            }
            aria-disabled={
                attemptDispute === undefined ||
                hasDisputingExpired ||
                hasDisputeLimitReached ||
                isOperationInProgress
            }
        >
            {t.labels.dispute}
        </GuxButton>
    ) : (
        <></>
    );
}

export function AgentSaveButton({
    attemptSaveByAgent,
}: {
    attemptSaveByAgent?: () => void;
}) {
    const t = useStrings(strings);

    const isDisputesFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.DISPUTES_TOGGLE);
    });

    const hasAgentReviewExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.agentReviewExpiry
        );
    });
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return (
        <div>
            <GuxButton
                accent="primary"
                onClick={() => {
                    attemptSaveByAgent?.();
                }}
                disabled={
                    attemptSaveByAgent === undefined ||
                    hasAgentReviewExpired ||
                    isOperationInProgress
                }
                aria-disabled={
                    attemptSaveByAgent === undefined ||
                    hasAgentReviewExpired ||
                    isOperationInProgress
                }
            >
                {isDisputesFeatureEnabled ? t.labels.signOff : t.labels.save}
            </GuxButton>
            {hasAgentReviewExpired && (
                <GuxTooltip>
                    {t.labels.agentReviewExpired({
                        agentReviewExpiry: DefaultLimits.agentReviewExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    );
}

export function CancelButton({
    disabled = false,
    ariaDisabled = false,
    onClick = () => {},
}: {
    disabled?: boolean;
    ariaDisabled?: boolean;
    onClick?: () => void;
}) {
    const t = useStrings(strings);
    return (
        <GuxButton
            accent="secondary"
            onClick={onClick}
            disabled={toGuxDisabled(disabled)}
            aria-disabled={toGuxDisabled(ariaDisabled)}
        >
            {t.labels.cancel}
        </GuxButton>
    );
}

export function ApplyCancelButton({
    setSideDrawerMode,
    onSelectedUser,
}: {
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
    onSelectedUser: (input: SearchResult | undefined) => void;
}) {
    const dispatch = useAppDispatch();
    return (
        <CancelButton
            onClick={() => {
                onSelectedUser(undefined);
                dispatch(usersSliceActions.clearUsers());
                dispatch(scorableSliceActions.cancelPrivateComments());
                setSideDrawerMode(SideDrawerMode.OFF);
            }}
        />
    );
}

export function AgentCancelButton() {
    return (
        <CancelButton
            onClick={() => {
                embeddingService.requestNavigation(
                    `#/analytics/agents/evaluations/me`
                );
            }}
        />
    );
}

export function RescoreButton({
    toggleRescoreMode,
}: {
    toggleRescoreMode: () => void;
}) {
    const t = useStrings(strings);

    const { hasEditScoreOrEditPermissions } = useContext(
        HasEditScoreOrEditPermissionsContext
    );

    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });

    const isDisputeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.DISPUTES_TOGGLE);
    });

    return hasEditScoreOrEditPermissions !== false ? (
        <div>
            <GuxButton
                id="rescore-button"
                accent="primary"
                onClick={() => {
                    toggleRescoreMode();
                    // previously we were also switching the overall evaluation status from COMPLETED -> IN-PROGRESS when we toggled rescore mode to true.
                    // We decided to drop that since it was inconsistent with the OLD UI.
                }}
                disabled={toGuxDisabled(hasScoringExpired)}
                aria-disabled={toGuxDisabled(hasScoringExpired)}
                data-testid="rescore-button"
            >
                {isDisputeFeatureEnabled
                    ? t.labels.reviseScore
                    : t.labels.rescore}
            </GuxButton>
            {hasScoringExpired && (
                <GuxTooltip>
                    {t.labels.scoringExpired({
                        scoringExpiry: DefaultLimits.scoringExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    ) : (
        <></>
    );
}

export function SubmitReleaseDateButton({
    releaseDateData,
    updateReleaseDateLabel,
    setExistingReleaseDate,
}: {
    releaseDateData: ReleaseDateSelectedType;
    updateReleaseDateLabel: (releaseDate: Date | undefined) => void;
    setExistingReleaseDate;
}) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });

    const timeZone = useTimeZone();
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return (
        <div className="SubmitReleaseDateButton">
            <GuxButton
                data-testid="release-date-submit-button"
                accent="tertiary"
                onClick={() => {
                    // re-calculate releaseDate
                    const updatedReleaseDate = getCurrentReleaseDate(
                        releaseDateData,
                        timeZone
                    );
                    dispatch(
                        saveEvaluationReleaseDate({
                            releaseDate: updatedReleaseDate,
                        })
                    ).then((e) => {
                        if ("error" in e) {
                            toastService.notifyMajorAlert(
                                "composer.evaluations.release.submitFail.title",
                                {
                                    bodyMessageKey:
                                        "composer.evaluations.release.submitFail.message",
                                }
                            );
                        } else {
                            setExistingReleaseDate(updatedReleaseDate);
                            updateReleaseDateLabel(updatedReleaseDate);
                            toastService.notifyMessage(
                                "composer.evaluations.release.submitSuccess.title",
                                {
                                    bodyMessageKey:
                                        "composer.evaluations.release.submitSuccess.message",
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                        }
                    });
                }}
                disabled={hasScoringExpired || isOperationInProgress}
                aria-disabled={hasScoringExpired || isOperationInProgress}
            >
                {t.labels.saveReleaseDate}
            </GuxButton>
            {hasScoringExpired && (
                <GuxTooltip>
                    {t.labels.releaseDateExpired({
                        releaseDateExpiry: DefaultLimits.releaseDateExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    );
}

export function AdminSubmitScorableButton({
    attemptSubmit,
    attemptSubmitAndAssign,
    releaseDateData,
}: {
    attemptSubmit: () => void;
    attemptSubmitAndAssign?: () => void;
    releaseDateData?: ReleaseDateSelectedType;
}) {
    const t = useStrings(strings);
    const formType = useAppSelector(selectFormType);
    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });
    const isEvaluationAssigneeFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_ASSIGNEE_FEATURE_TOGGLE_NAME
        );
    });
    const hasAssignPermission = useAppSelector(selectHasAssignPermission);
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const dateTimeNow = new Date();
    const isDateInPast =
        releaseDateData &&
        (releaseDateData.type === ReleaseDateOptions.NOW ||
            ((releaseDateData.type === ReleaseDateOptions.EXISTING ||
                releaseDateData.type === ReleaseDateOptions.CUSTOM) &&
                releaseDateData.time &&
                releaseDateData.time.valueOf() <= dateTimeNow.valueOf()));
    const useMultiButtonDropdown =
        isEvaluationAssigneeFeatureEnabled &&
        hasAssignPermission &&
        formType === FormType.EVALUATION &&
        attemptSubmitAndAssign &&
        isNotCalibration &&
        !isDateInPast;

    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    const submitOnly = () => {
        attemptSubmit();
    };

    return (
        <div>
            {useMultiButtonDropdown ? (
                <GuxButtonMulti
                    accent="primary"
                    disabled={hasScoringExpired || isOperationInProgress}
                    aria-disabled={hasScoringExpired || isOperationInProgress}
                    data-testid="submit-operations-dropdown"
                >
                    <span slot="title">{t.labels.submit}</span>
                    <GuxListItem
                        data-testid="dropdown-submit-action"
                        onClick={submitOnly}
                    >
                        {t.labels.submit}
                    </GuxListItem>
                    <GuxListItem
                        data-testid="dropdown-submit-and-assign-action"
                        onClick={() => {
                            attemptSubmitAndAssign();
                        }}
                    >
                        {t.labels.submitAndAssign}
                    </GuxListItem>
                </GuxButtonMulti>
            ) : (
                <GuxButton
                    accent="primary"
                    onClick={submitOnly}
                    disabled={hasScoringExpired || isOperationInProgress}
                    aria-disabled={hasScoringExpired || isOperationInProgress}
                    data-testid="submit-button"
                >
                    {t.labels.submit}
                </GuxButton>
            )}
            {hasScoringExpired && (
                <GuxTooltip>
                    {t.labels.scoringExpired({
                        scoringExpiry: DefaultLimits.scoringExpiry,
                    })}
                </GuxTooltip>
            )}
            <React.Suspense fallback={null}>
                <div hidden={true}>
                    <GuxButtonMulti />
                </div>
            </React.Suspense>
        </div>
    );
}

export function AdminSaveScorableButton({
    isAdmin,
    attemptSave,
    rescoreMode,
    toggleRescoreMode,
}: {
    isAdmin: boolean;
    attemptSave: () => void;
    rescoreMode: boolean;
    toggleRescoreMode: () => void;
}) {
    const formType = useAppSelector(selectFormType);

    const canSave = formType === FormType.SURVEY || isAdmin;

    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    const t = useStrings(strings);
    return (
        <div>
            <GuxButton
                accent="secondary"
                onClick={() => {
                    if (canSave) attemptSave();

                    rescoreMode && toggleRescoreMode();
                }}
                disabled={hasScoringExpired || isOperationInProgress}
                aria-disabled={hasScoringExpired || isOperationInProgress}
            >
                {t.labels.save}
            </GuxButton>
            {hasScoringExpired && (
                <GuxTooltip>
                    {t.labels.scoringExpired({
                        scoringExpiry: DefaultLimits.scoringExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    );
}

export function DeclineDisputeButton({
    attemptDisputeDecline,
}: {
    attemptDisputeDecline?: () => void;
}) {
    const t = useStrings(strings);
    const hasDisputeDeclinePermission = useAppSelector(
        selectHaveDisputeDeclinePermission
    );

    const hasDisputingExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.disputeExpiry
        );
    });
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return hasDisputeDeclinePermission && attemptDisputeDecline ? (
        <GuxButton
            accent="secondary"
            disabled={hasDisputingExpired || isOperationInProgress}
            aria-disabled={hasDisputingExpired || isOperationInProgress}
            onClick={() => {
                attemptDisputeDecline();
            }}
        >
            {t.labels.decline}
        </GuxButton>
    ) : (
        <></>
    );
}

export function CancelRescoreModeButton({
    toggleRescoreMode,
    rescoreMode,
}: {
    toggleRescoreMode: () => void;
    rescoreMode: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });

    return rescoreMode ? (
        <div>
            <GuxButton
                accent="secondary"
                onClick={() => {
                    toggleRescoreMode();
                    dispatch(cancelEvaluationRescoring());
                }}
                disabled={toGuxDisabled(hasScoringExpired)}
                aria-disabled={toGuxDisabled(hasScoringExpired)}
            >
                {t.labels.cancel}
            </GuxButton>
            {hasScoringExpired && (
                <GuxTooltip>
                    {t.labels.scoringExpired({
                        scoringExpiry: DefaultLimits.scoringExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    ) : (
        <></>
    );
}

export function DeleteEvaluationButton() {
    const dispatch = useAppDispatch();
    const hasDeletingExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.deletingExpiry
        );
    });
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    const t = useStrings(strings);

    return (
        <div>
            <GuxButton
                onClick={() => {
                    dispatch(deleteScorable()).then((e) => {
                        if ("error" in e) {
                            toastService.notifyMinorAlert(
                                "playback.nav.evaluation.not.deleted.title",
                                {
                                    bodyMessageKey:
                                        "playback.nav.evaluation.not.deleted.msg",
                                }
                            );
                        } else {
                            toastService.notifyMessage(
                                "playback.nav.evaluation.deleted.title",
                                {
                                    bodyMessageKey:
                                        "playback.nav.evaluation.deleted.msg",
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                        }
                    });
                }}
                disabled={hasDeletingExpired || isOperationInProgress}
                aria-disabled={hasDeletingExpired || isOperationInProgress}
            >
                <GuxIcon
                    icon-name="delete"
                    screenreader-text={t.labels.delete}
                ></GuxIcon>
            </GuxButton>
            {hasDeletingExpired && (
                <GuxTooltip>
                    {t.labels.deletingExpired({
                        deletingExpiry: DefaultLimits.deletingExpiry,
                    })}
                </GuxTooltip>
            )}
        </div>
    );
}

export function CreateCalibrationButton({
    toggleCalibrationMode,
    calibrationError,
}: {
    toggleCalibrationMode?: () => void;
    calibrationError: (response: string) => void;
}) {
    const dispatch = useAppDispatch();
    const noCalibrationEvaluators = !useAppSelector(
        selectCalibrationEvaluators
    );
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);
    const t = useStrings(strings);

    return (
        <GuxButton
            accent="primary"
            disabled={noCalibrationEvaluators || isOperationInProgress}
            aria-disabled={noCalibrationEvaluators || isOperationInProgress}
            onClick={() => {
                dispatch(createCalibration())
                    .then((response) => {
                        if (!("error" in response) && "payload" in response) {
                            toggleCalibrationMode?.();
                            toastService.notifyMessage(
                                t.createCalibration.success.title,
                                {
                                    bodyMessageKey:
                                        t.createCalibration.success.message,
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                            dispatch(usersSliceActions.clearUsers());
                            dispatch(
                                scorableSliceActions.clearCalibrationEvaluators()
                            );
                            const destination = `#/analytics/interactions/${response.payload?.conversation?.id}/admin/calibrations/${response.payload?.id}`;
                            embeddingService.requestNavigation(destination);
                        }
                    })
                    .catch((error) => {
                        toastService.notifyMinorAlert(
                            t.createCalibration.fail.title,
                            {
                                bodyMessageKey:
                                    t.createCalibration.fail.message,
                            }
                        );
                        calibrationError(error.message);
                    });
            }}
        >
            {t.labels.createCalibration}
        </GuxButton>
    );
}
